import { MutationTree } from 'vuex';
import { ActionStatus } from '@/application/types';
import { ProductDataDistribution, ProductIdentification, ProductMasterData, ProductOrders, ProductRelatedSPPs, ProductRawImport, ProductRelatedCalendars, ProductSearchState } from '../types';

export const productSearchMutations: MutationTree<ProductSearchState> = {

  clear(state) {
    state.currentProductId = null;
    state.currentProductIdentification = null;
    state.currentProductMasterData = null;
    state.currentProductOrders = null;
    state.currentProductRelatedCalendars = null;
    state.currentProductRelatedSPPs = null;
    state.currentProductRawImport = null;
    state.currentProductDataDistribution = null;

    state.productIdentifications = [];
    state.hasSearchBeenTriggered = false;
    state.hasSearchResultedIn0Results = false;
    state.lastSearchType = 'ean';

    state.getProductIdentificationsByEANStatus = ActionStatus.None;
    state.getProductIdentificationsByProjectNumberStatus = ActionStatus.None;
    state.getProductIdentificationStatus = ActionStatus.None;
    state.getProductMasterDataStatus = ActionStatus.None;
    state.getProductDataDistributionStatus = ActionStatus.None;
    state.getProductOrdersStatus = ActionStatus.None;
    state.getProductRelatedCalendarsStatus = ActionStatus.None;
    state.getProductRelatedSPPsStatus = ActionStatus.None;
    state.getProductRawImportStatus = ActionStatus.None;
    state.rescheduleJobStatus = ActionStatus.None;
  },

  setCurrentProductId(state, productId: string): void {
    state.currentProductId = productId;
  },

  // Update search term
  updateLastSearchTerm(state, searchTerm: string) {
    state.lastSearchTerm = searchTerm;
  },

  // Get product identifications by EAN

  getProductIdentificationsByEANStarted(state) {
    state.lastSearchType = 'ean';
    state.getProductIdentificationsByEANStatus = ActionStatus.InProgress;
  },

  getProductIdentificationsByEANSucceeded(state, productIdentifications: ProductIdentification[]) {
    state.productIdentifications = productIdentifications;
    state.hasSearchBeenTriggered = true;
    state.hasSearchResultedIn0Results = productIdentifications.length === 0;
    state.getProductIdentificationsByEANStatus = ActionStatus.Successful;
  },

  getProductIdentificationsByEANFailed(state) {
    state.getProductIdentificationsByEANStatus = ActionStatus.Failed;
  },

  // Get product identifications by project number

  getProductIdentificationsByProjectNumberStarted(state) {
    state.lastSearchType = 'project';
    state.getProductIdentificationsByProjectNumberStatus = ActionStatus.InProgress;
  },

  getProductIdentificationsByProjectNumberSucceeded(state, productIdentifications: ProductIdentification[]) {
    state.productIdentifications = productIdentifications;
    state.hasSearchBeenTriggered = true;
    state.hasSearchResultedIn0Results = productIdentifications.length === 0;
    state.getProductIdentificationsByProjectNumberStatus = ActionStatus.Successful;
  },

  getProductIdentificationsByProjectNumberFailed(state) {
    state.getProductIdentificationsByProjectNumberStatus = ActionStatus.Failed;
  },

  // Get product identification

  getProductIdentificationStarted(state) {
    state.getProductIdentificationStatus = ActionStatus.InProgress;
  },

  getProductIdentificationSucceeded(state, productIdentification: ProductIdentification) {
    state.currentProductIdentification = productIdentification;
    state.hasSearchBeenTriggered = true;
    state.hasSearchResultedIn0Results = false;
    state.getProductIdentificationStatus = ActionStatus.Successful;
  },

  getProductIdentificationFailed(state) {
    state.getProductIdentificationStatus = ActionStatus.Failed;
  },

  // Get product master data

  getProductMasterDataStarted(state) {
    state.getProductMasterDataStatus = ActionStatus.InProgress;
  },

  getProductMasterDataSucceeded(state, productMasterData: ProductMasterData) {
    state.currentProductMasterData = productMasterData;
    state.getProductMasterDataStatus = ActionStatus.Successful;
  },

  getProductMasterDataFailed(state) {
    state.getProductMasterDataStatus = ActionStatus.Failed;
  },

  // Get product data distribution

  getProductDataDistributionStarted(state) {
    state.getProductDataDistributionStatus = ActionStatus.InProgress;
  },

  getProductDataDistributionSucceeded(state, productDataDistribution: ProductDataDistribution) {
    state.currentProductDataDistribution = productDataDistribution;
    state.getProductDataDistributionStatus = ActionStatus.Successful;
  },

  getProductDataDistributionFailed(state) {
    state.getProductDataDistributionStatus = ActionStatus.Failed;
  },

  // Get product orders

  getProductOrdersStarted(state) {
    state.getProductOrdersStatus = ActionStatus.InProgress;
  },

  getProductOrdersSucceeded(state, productOrders: ProductOrders) {
    state.currentProductOrders = productOrders;
    state.getProductOrdersStatus = ActionStatus.Successful;
  },

  getProductOrdersFailed(state) {
    state.getProductOrdersStatus = ActionStatus.Failed;
  },

  // Get product related calendars

  getProductRelatedCalendarsStarted(state) {
    state.getProductRelatedCalendarsStatus = ActionStatus.InProgress;
  },

  getProductRelatedCalendarsSucceeded(state, productRelatedCalendars: ProductRelatedCalendars) {
    state.currentProductRelatedCalendars = productRelatedCalendars;
    state.getProductRelatedCalendarsStatus = ActionStatus.Successful;
  },

  getProductRelatedCalendarsFailed(state) {
    state.getProductRelatedCalendarsStatus = ActionStatus.Failed;
  },

  // Get product related ssbs

  getProductRelatedSPPsStarted(state) {
    state.getProductRelatedSPPsStatus = ActionStatus.InProgress;
  },

  getProductRelatedSPPsSucceeded(state, productRelatedSPPs: ProductRelatedSPPs) {
    state.currentProductRelatedSPPs = productRelatedSPPs;
    state.getProductRelatedSPPsStatus = ActionStatus.Successful;
  },

  getProductRelatedSPPsFailed(state) {
    state.getProductRelatedSPPsStatus = ActionStatus.Failed;
  },

  // Get product raw import

  getProductRawImportStarted(state) {
    state.getProductRawImportStatus = ActionStatus.InProgress;
  },

  getProductRawImportSucceeded(state, productRawImport: ProductRawImport) {
    state.currentProductRawImport = productRawImport;
    state.getProductRawImportStatus = ActionStatus.Successful;
  },

  getProductRawImportFailed(state) {
    state.getProductRawImportStatus = ActionStatus.Failed;
  },

  // Reschedule job

  rescheduleJobStarted(state) {
    state.rescheduleJobStatus = ActionStatus.InProgress;
  },

  rescheduleJobSucceeded(state) {
    state.rescheduleJobStatus = ActionStatus.Successful;
  },

  rescheduleJobFailed(state) {
    state.rescheduleJobStatus = ActionStatus.Failed;
  },

};
